import { useLazyQuery } from '@apollo/client/react';
import { useTheme } from '@emotion/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback, useEffect, useMemo } from 'react';

import { Box, Button, Flex, Loader, Modal, Text } from '~/components/blocks';
import {
  GetFollowupSettingGuestPatientProfileDocument,
  GetFollowupSettingPatientProfileDocument,
} from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import { followupMessageSettingModalState } from '~/state/partials/followup_message_setting_modal/atoms';
import { messageModalState } from '~/state/partials/message_modal/atoms';
import { Panel } from '~/state/partials/message_modal/types';
import { patientDetailDialogState } from '~/state/partials/patient_detail_dialog/atoms';
import { TabType } from '~/state/partials/patient_detail_dialog/types';

export const FollowupMessageSettingModal = () => {
  const theme = useTheme();

  const { isOpen, patientId, guestPatientId } = useAtomValue(followupMessageSettingModalState);
  const resetFollowupMessageSettingModalState = useResetAtom(followupMessageSettingModalState);

  const [getPatientProfile, { data: patientData, loading: patientLoading }] = useLazyQuery(
    GetFollowupSettingPatientProfileDocument,
  );
  const [getGuestPatientProfile, { data: guestPatientData, loading: guestPatientLoading }] =
    useLazyQuery(GetFollowupSettingGuestPatientProfileDocument);
  const setMessageModalState = useSetAtom(messageModalState);
  const setPatientDetailDialogState = useSetAtom(patientDetailDialogState);

  const isLoading = patientLoading || guestPatientLoading;
  const patient = useMemo(() => {
    if (patientId) {
      return getNode(patientData, 'Patient');
    }
    if (guestPatientId) {
      if (guestPatientData?.node?.__typename === 'DirectVisitor') {
        return getNode(guestPatientData, 'DirectVisitor');
      }
      if (guestPatientData?.node?.__typename === 'WebVisitor') {
        return getNode(guestPatientData, 'WebVisitor');
      }
      if (guestPatientData?.node?.__typename === 'LineVisitor') {
        return getNode(guestPatientData, 'LineVisitor');
      }
    }
  }, [guestPatientData, guestPatientId, patientData, patientId]);
  const patientName = patient ? `${patient.familyName}${patient.givenName}` : undefined;

  const handleClose = useCallback(
    () => resetFollowupMessageSettingModalState(),
    [resetFollowupMessageSettingModalState],
  );
  const handleClick = useCallback(() => {
    if (!patient) return;

    resetFollowupMessageSettingModalState();

    // TODO: 旧モーダルをパートナー内に移動する時にstateを分ける
    // 連携画面ではメッセージ送信モーダルのみを、店舗画面では患者詳細ダイアログのみを開くようになっており、両者が一画面上に同時に表示されることはないため良しとしている
    setMessageModalState((_state) => ({
      ..._state,
      isOpen: true,
      currentPanel: Panel.initial,
      patientId: patient.id,
    }));
    setPatientDetailDialogState((_state) => ({
      ..._state,
      isOpen: true,
      type: 'message' as TabType,
      patientId: patient.id,
    }));
  }, [
    patient,
    resetFollowupMessageSettingModalState,
    setMessageModalState,
    setPatientDetailDialogState,
  ]);

  useEffect(() => {
    if (patientId) {
      getPatientProfile({
        variables: {
          id: patientId,
        },
      });
    }

    if (guestPatientId) {
      getGuestPatientProfile({
        variables: {
          id: guestPatientId,
        },
      });
    }
  }, [getGuestPatientProfile, getPatientProfile, guestPatientId, patientId]);

  return (
    <Modal open={isOpen} size="xs" onClose={handleClose}>
      {isLoading ? (
        <Modal.Body>
          <Box position="relative" height="240px">
            <Loader open inside />
          </Box>
        </Modal.Body>
      ) : (
        <>
          <Modal.Header>
            <Text paddingRight={theme.space.xxxl}>
              {patientName || 'ー'}様に服薬フォローアップの設定を行いますか？
            </Text>
          </Modal.Header>
          <Modal.Body>
            <Text size="s">
              服薬フォローアップが必要な患者に、メッセージや質問票を送信できます。
            </Text>
          </Modal.Body>
          <Modal.Footer>
            <Flex flexDirection="column" justifyContent="center">
              <Button use="base" ml={theme.space.m} onClick={handleClick}>
                設定
              </Button>
            </Flex>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};
